
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Mail, Phone, MessagesSquare, Video, 
  CheckCircle2, Clock, Users, HelpCircle, HeadphonesIcon 
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FeaturePageLayout } from "@/components/layout/FeaturePageLayout";

const TechnicalSupport = () => {
  const { t } = useTranslation("support");

  const supportOptions = [
    {
      icon: <Mail className="h-6 w-6" />,
      title: t("support.technical.options.email.title"),
      description: t("support.technical.options.email.description"),
      action: t("support.technical.options.email.action"),
      info: t("support.technical.options.email.info")
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: t("support.technical.options.phone.title"),
      description: t("support.technical.options.phone.description"),
      action: t("support.technical.options.phone.action"),
      info: t("support.technical.options.phone.info")
    },
    {
      icon: <MessagesSquare className="h-6 w-6" />,
      title: t("support.technical.options.chat.title"),
      description: t("support.technical.options.chat.description"),
      action: t("support.technical.options.chat.action"),
      info: t("support.technical.options.chat.info")
    },
    {
      icon: <Video className="h-6 w-6" />,
      title: t("support.technical.options.video.title"),
      description: t("support.technical.options.video.description"),
      action: t("support.technical.options.video.action"),
      info: t("support.technical.options.video.info")
    }
  ];
  
  const faqItems = [
    {
      question: t("support.technical.faq.hours.question"),
      answer: t("support.technical.faq.hours.answer")
    },
    {
      question: t("support.technical.faq.response.question"),
      answer: t("support.technical.faq.response.answer")
    },
    {
      question: t("support.technical.faq.onboarding.question"),
      answer: t("support.technical.faq.onboarding.answer")
    },
    {
      question: t("support.technical.faq.plans.question"),
      answer: t("support.technical.faq.plans.answer")
    }
  ];

  return (
    <FeaturePageLayout
      title={t("support.technical.title")}
      subtitle={t("support.technical.subtitle")}
      description={t("support.technical.description")}
      icon={<HeadphonesIcon className="h-10 w-10 text-white" />}
      bgColor="bg-gradient-to-r from-primary to-blue-700"
      ctaText="Kontakt Support"
      ctaLink="mailto:support@offertio.dk"
      secondaryCtaText="Læs mere om vores planer"
      secondaryCtaLink="/pricing"
    >
      <div className="space-y-16">
        {/* Our Support Promise */}
        <div>
          <h2 className="text-3xl font-bold mb-6 text-center">{t("support.technical.promise.title")}</h2>
          <p className="text-lg mb-8 text-center text-gray-700 max-w-3xl mx-auto">
            {t("support.technical.promise.description")}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <CheckCircle2 className="mr-2 h-5 w-5 text-primary" />
                  {t("support.technical.promise.responsive.title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t("support.technical.promise.responsive.description")}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <Users className="mr-2 h-5 w-5 text-primary" />
                  {t("support.technical.promise.knowledgeable.title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t("support.technical.promise.knowledgeable.description")}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <Clock className="mr-2 h-5 w-5 text-primary" />
                  {t("support.technical.promise.multilingual.title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {t("support.technical.promise.multilingual.description")}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Support Options */}
        <div>
          <h2 className="text-3xl font-bold mb-4 text-center">{t("support.technical.options.title")}</h2>
          <p className="text-lg mb-8 text-center text-gray-700 max-w-3xl mx-auto">
            {t("support.technical.options.description")}
          </p>
          
          <Tabs defaultValue="email" className="max-w-5xl mx-auto">
            <TabsList className="grid grid-cols-4 mb-8">
              <TabsTrigger value="email">
                <Mail className="mr-2 h-4 w-4 md:mr-0 md:mb-1" />
                <span className="hidden md:block">{t("support.technical.options.email.title")}</span>
              </TabsTrigger>
              <TabsTrigger value="phone">
                <Phone className="mr-2 h-4 w-4 md:mr-0 md:mb-1" />
                <span className="hidden md:block">{t("support.technical.options.phone.title")}</span>
              </TabsTrigger>
              <TabsTrigger value="chat">
                <MessagesSquare className="mr-2 h-4 w-4 md:mr-0 md:mb-1" />
                <span className="hidden md:block">{t("support.technical.options.chat.title")}</span>
              </TabsTrigger>
              <TabsTrigger value="video">
                <Video className="mr-2 h-4 w-4 md:mr-0 md:mb-1" />
                <span className="hidden md:block">{t("support.technical.options.video.title")}</span>
              </TabsTrigger>
            </TabsList>
            
            {supportOptions.map((option, index) => (
              <TabsContent key={index} value={Object.keys({ email: "", phone: "", chat: "", video: "" })[index]}>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      {option.icon}
                      <span className="ml-2">{option.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">{option.description}</p>
                    <p className="text-sm font-medium text-primary">{option.info}</p>
                  </CardContent>
                  <CardFooter>
                    <Button>
                      {option.action}
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
        
        {/* FAQ */}
        <div>
          <h2 className="text-3xl font-bold mb-4 text-center">{t("support.technical.faq.title")}</h2>
          <p className="text-lg mb-8 text-center text-gray-700 max-w-3xl mx-auto">
            {t("support.technical.faq.description")}
          </p>
          
          <Accordion type="single" collapsible className="max-w-3xl mx-auto">
            {faqItems.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200">
                <AccordionTrigger className="text-left py-4">
                  <div className="flex gap-2">
                    <HelpCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pl-7">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        
        {/* Self-service Resources */}
        <div>
          <h2 className="text-3xl font-bold mb-4 text-center">{t("support.technical.resources.title")}</h2>
          <p className="text-lg mb-8 text-center text-gray-700 max-w-3xl mx-auto">
            {t("support.technical.resources.description")}
          </p>
          
          <div className="flex justify-center gap-4 flex-wrap">
            <Button variant="outline" className="min-w-[180px]">
              {t("support.technical.resources.knowledgeBase")}
            </Button>
            <Button variant="outline" className="min-w-[180px]">
              {t("support.technical.resources.videoTutorials")}
            </Button>
            <Button variant="outline" className="min-w-[180px]">
              {t("support.technical.resources.userGuides")}
            </Button>
          </div>
        </div>
      </div>
    </FeaturePageLayout>
  );
};

export default TechnicalSupport;
